import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Login as LoginIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { setJITToken, setJITUser, parseJITToken } from '../../services/jitApi';

// Read a cookie by name
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Clear the JIT SSO token cookie after reading it
function clearJITSSOCookie() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Clear on exact path and root path, across domain variants
  document.cookie = `jit_sso_token=; Path=/sso-callback; Max-Age=0`;
  document.cookie = `jit_sso_token=; Path=/; Max-Age=0`;
  for (let i = 0; i < parts.length; i++) {
    const domain = '.' + parts.slice(i).join('.');
    document.cookie = `jit_sso_token=; Domain=${domain}; Path=/sso-callback; Max-Age=0`;
    document.cookie = `jit_sso_token=; Domain=${domain}; Path=/; Max-Age=0`;
  }

  // Also clear legacy cookie names
  document.cookie = `jit_token=; Path=/sso-callback; Max-Age=0`;
  document.cookie = `jit_token=; Path=/; Max-Age=0`;
  for (let i = 0; i < parts.length; i++) {
    const domain = '.' + parts.slice(i).join('.');
    document.cookie = `jit_token=; Domain=${domain}; Path=/sso-callback; Max-Age=0`;
    document.cookie = `jit_token=; Domain=${domain}; Path=/; Max-Age=0`;
  }
}

const JITSSOCallback = ({ onLogin }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent running multiple times
    if (processedRef.current) {
      return;
    }
    processedRef.current = true;

    const handleToken = async () => {
      // DEBUG: Log all available info for diagnosing SSO callback issues
      console.log('[JIT SSO Debug] Callback started');
      console.log('[JIT SSO Debug] URL:', window.location.href);
      console.log('[JIT SSO Debug] All cookies:', document.cookie);
      console.log('[JIT SSO Debug] Hash:', window.location.hash);

      // Read token from cookie (primary) or hash fragment (fallback for in-flight redirects)
      let token = getCookie('jit_sso_token');
      console.log('[JIT SSO Debug] Cookie jit_sso_token:', token ? `found (${token.length} chars)` : 'NOT FOUND');

      if (!token) {
        // Fallback: try hash fragment for backwards compatibility with in-flight redirects
        const hash = window.location.hash;
        console.log('[JIT SSO Debug] Trying hash fragment fallback, hash:', hash ? hash.substring(0, 50) + '...' : '(empty)');
        if (hash.startsWith('#token=')) {
          token = decodeURIComponent(hash.substring(7));
          console.log('[JIT SSO Debug] Token from hash:', token ? `found (${token.length} chars)` : 'NOT FOUND');
        }
      }

      // Clean up immediately — remove cookie and hash for security
      clearJITSSOCookie();
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      if (!token) {
        console.error('[JIT SSO Debug] NO TOKEN FOUND from cookie or hash. Cookie domain issue or proxy did not set cookie.');
        setError('No authentication token found. Please try logging in again.');
        setLoading(false);
        return;
      }

      try {
        // Parse token to get claims
        const claims = parseJITToken(token);
        if (!claims) {
          throw new Error('Invalid token format');
        }

        // Store token and user info in localStorage
        setJITToken(token);
        setJITUser({
          email: claims.email,
          customerId: claims.customerId,
          realm: claims.realm,
          role: claims.role || 'viewer',
        });

        // Show success state
        setLoading(false);
        setSuccess(true);
        toast.success('Authentication successful!');

        // After success animation, notify parent to transition to dashboard
        setTimeout(() => {
          if (onLogin) {
            onLogin({
              token,
              email: claims.email,
              customerId: claims.customerId,
              realm: claims.realm,
              role: claims.role || 'viewer',
            });
          }
        }, 1000);
      } catch (err) {
        console.error('Error processing JIT token:', err);
        setError('Failed to process authentication. Please try logging in again.');
        setLoading(false);
        toast.error('Authentication failed');
      }
    };

    handleToken();
  }, [onLogin]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F9FBFD',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '12px',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
            }}
          >
            {/* Logo */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img
                src="https://www.cloudkeeper.com/themes/cloud_theme/images/Cloudkeeper_New.svg"
                alt="CloudKeeper"
                style={{
                  height: '40px',
                  width: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px'
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                }}
              >
                JIT Access Portal
              </Typography>
            </Box>

            {/* Loading State */}
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(70, 152, 211, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{ color: '#4698D3' }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#253E66',
                    mb: 1,
                  }}
                >
                  Completing Authentication
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                    mb: 2,
                  }}
                >
                  Please wait while we verify your credentials...
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 0.5,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#4698D3',
                        }}
                      />
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            )}

            {/* Success State */}
            {success && !loading && !error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(39, 174, 96, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 32, color: '#27AE60' }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#27AE60',
                    mb: 1,
                  }}
                >
                  Authentication Successful
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                  }}
                >
                  Redirecting to JIT Portal...
                </Typography>
              </motion.div>
            )}

            {/* Error State */}
            {error && !loading && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <ErrorIcon sx={{ fontSize: 32, color: '#E74C3C' }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#E74C3C',
                    mb: 1.5,
                  }}
                >
                  Authentication Failed
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                    border: '1px solid rgba(231, 76, 60, 0.2)',
                    borderRadius: '8px',
                    p: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666666',
                      fontFamily: 'Metropolis, sans-serif',
                      lineHeight: 1.6,
                    }}
                  >
                    {error}
                  </Typography>
                </Paper>

                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={() => window.location.href = '/'}
                  fullWidth
                  sx={{
                    backgroundColor: '#4698D3',
                    borderRadius: '6px',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 1.5,
                    boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
                    '&:hover': {
                      backgroundColor: '#3580B8',
                      boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
                    },
                  }}
                >
                  Return to Login
                </Button>
              </motion.div>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #E5E5E5' }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#BDBDBD',
                  fontFamily: 'Metropolis, sans-serif',
                }}
              >
                JIT Access Portal powered by CK Prism
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default JITSSOCallback;
